// Decides what a visitor sees (GEN-20, ADR-086): first-run setup before any account exists, sign-in when signed out,
// otherwise the shell for the signed-in role. A deactivated or unknown account is signed out with the reason shown.
// The business language comes from the server before and after sign-in (GEN-19).
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Notice } from '../design/kit';
import { hasSession, loadMe, onSessionChange, Refusal, setupStatus, signOut } from '../data/auth';
import { supabase, supabaseConfigured } from '../data/supabase';
import { ChangePassword } from '../features/auth/ChangePassword';
import { FirstRun } from '../features/auth/FirstRun';
import { SignIn } from '../features/auth/SignIn';
import { applyLanguage, isLanguage, rememberBusinessLanguage, type Language } from '../i18n';
import { userLanguage } from '../lib/ux/preferences';
import { messageKey } from '../lib/errors';
import { SessionProvider, type Session } from './session';

type State =
  | { kind: 'loading' }
  | { kind: 'setup'; language: Language }
  | { kind: 'signin'; refusal?: string | undefined }
  | { kind: 'password'; name: string }
  | { kind: 'ready'; session: Session }
  | { kind: 'error'; code: string };

export function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Several checks can overlap (start-up, sign-in, Auth events). Only the latest one may change the screen or sign
  // out: an older check holding a stale session must never sign out a fresh one.
  const latest = useRef(0);
  // Why the gate itself signed the user out. Its sign-out fires an Auth event whose newer check must still show it.
  const reason = useRef<string>();

  const resolve = useCallback(async () => {
    const run = ++latest.current;
    const current = () => run === latest.current;
    try {
      if (!(await hasSession())) {
        const status = await setupStatus();
        if (!current()) return;
        const language: Language = isLanguage(status.language) ? status.language : 'ar';
        await applyLanguage(language);
        const refusal = reason.current;
        reason.current = undefined;
        setState(status.setUp ? { kind: 'signin', refusal } : { kind: 'setup', language });
        return;
      }
      const me = await loadMe();
      if (!current()) return;
      if (!me || !me.active) {
        reason.current = me ? 'ACCOUNT_DEACTIVATED' : 'NOT_SIGNED_IN';
        await signOut();
        const status = await setupStatus();
        if (!current()) return;
        await applyLanguage(isLanguage(status.language) ? status.language : 'ar');
        setState({ kind: 'signin', refusal: reason.current });
        reason.current = undefined;
        return;
      }
      const { data: settings } = await supabase.from('v_settings').select('language').maybeSingle();
      if (!current()) return;
      const language = (settings as { language?: string } | null)?.language;
      const business: Language = isLanguage(language) ? language : 'ar';
      rememberBusinessLanguage(business);
      // ADR-110: the person's own language when they chose one, the business language otherwise.
      await applyLanguage(userLanguage(me.user_id) ?? business);
      // SCR-FND-08: a temporary password must be replaced before any other page (GEN-20, ADR-086).
      if (me.must_change_password) {
        setState({ kind: 'password', name: me.display_name });
        return;
      }
      setState({ kind: 'ready', session: { userId: me.user_id, role: me.role, name: me.display_name } });
    } catch (err) {
      if (current()) setState({ kind: 'error', code: err instanceof Refusal ? err.code : 'SERVER_ERROR' });
    }
  }, []);

  useEffect(() => {
    void resolve();
    // Supabase advises not to call Supabase inside its auth callback: defer the check to the next tick.
    return onSessionChange(() => {
      setTimeout(() => void resolve(), 0);
    });
  }, [resolve]);

  if (!supabaseConfigured) {
    return (
      <main className="yv-auth">
        <div className="yv-auth__panel">
          <Notice tone="danger">VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing (apps/web/.env.local).</Notice>
        </div>
      </main>
    );
  }
  switch (state.kind) {
    case 'loading':
      return (
        <main className="yv-auth" aria-busy="true">
          <p className="yv-auth__lead">{t('loading')}</p>
        </main>
      );
    case 'setup':
      return <FirstRun language={state.language} onDone={() => void resolve()} />;
    case 'signin':
      return <SignIn initialRefusal={state.refusal} onSignedIn={() => void resolve()} />;
    case 'error':
      return (
        <main className="yv-auth">
          <div className="yv-auth__panel">
            <Notice tone={state.code === 'OFFLINE' ? 'warning' : 'danger'}>{t(messageKey(state.code))}</Notice>
          </div>
        </main>
      );
    case 'password':
      return <ChangePassword name={state.name} onChanged={() => void resolve()} />;
    case 'ready':
      return <SessionProvider session={state.session}>{children}</SessionProvider>;
  }
}
